import {useEffect, useRef, useState} from "react";
import Box from '@mui/material/Box';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { useFormik } from "formik";
import { Alert, Button, Checkbox, Divider, Grid, Typography, Paper } from "@mui/material";
import styled from "@emotion/styled";

import { getInitialValues, PCF_CONSTANTS } from "./helpers";
import { FormEvalSection } from './FormEvalSection';
import { FormField } from './FormField';
import { FormFieldWrapper } from './FormFieldWrapper';
import { FormHeader } from './FormHeader';
import { FormRadioGroup } from './FormRadioGroup';
import { FormSuccess } from "./FormSuccess";

export const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    paddingBottom: '56px',
    position: 'relative',
  
    '@media (min-width: 900px)': {
      paddingBottom: '32px'
    }
  });
  
  export const Image = styled.img({
    bottom: 0,
    height: '26px',
    left: '50%',
    position: 'absolute',
    transform: 'translateX(-50%)',
  
    '@media (min-width: 900px)': {
      left: 'calc(100% - 110px)',
      top: '40px'
    }
  });

const PotentialCandidateForm = ({
    idealCandidate,
    onSubmit,
    potentialCandidate,
    includeHeader,
    viewOnly,
    showSuccess
}) => {
    const headerRef= useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);
  
    useEffect(() => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
      }
    }, [])

    const [attestation, setAttestation] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [errors, setErrors] = useState(null)

    const formik = useFormik({
        initialValues: getInitialValues(idealCandidate, potentialCandidate),
        validationSchema: PCF_CONSTANTS.FORM_VALIDATION,
        onSubmit: async ({ name, email, skills, behaviors, gender, race, phone }) => {
            setErrors(null);
            const lowerEmail = email ? email.toLowerCase() : email;
            const res = await onSubmit({ name, skills, behaviors, gender, race, phone, email: lowerEmail });
            if (res?.code === 422) {
                setErrors(res.errors)
            } else {
                setCompleted(true);
            }
        }
    });

    const handleChange = (dataSet, id, label, value) => {
        const data = formik.values[dataSet];
        formik.setFieldValue(dataSet, {
            ...data,
            [id]: { ...data[id], [label]: value }
        })
    };

    function handlePhoneBlur() {
        if (!formik.touched.phone) formik.setTouched({ ...formik.touched, phone: true });

        if (!formik.values.phone || !matchIsValidTel(formik.values.phone)) {
            formik.setFieldError('phone', 'Phone is required')
        } else {
            const { phone, ...rest } = formik.errors; 
            formik.setFieldError({ ...rest });
        }
    };

    function handlePhoneChange(value) {
        if (!formik.touched.phone) formik.setTouched({ ...formik.touched, phone: true });

        formik.setFieldValue('phone', value);

        const { phone, ...rest } = formik.errors; 
        formik.setFieldError({ ...rest });
    };

    function handleValidateRequiredFields() {
        const requiredFields = ['name', 'email', 'phone'];
        return attestation && requiredFields.every(field => formik.touched[field] && !formik.errors[field]);
    }

    if (completed) {
        if(showSuccess) {
            return <FormSuccess idealCandidate={idealCandidate} includeHeader={includeHeader}/>
        }
    }

    return (
    <form onSubmit={formik.handleSubmit}>
        <Box display="flex" flexDirection="column" alignItems="center" sx={{
            paddingTop: includeHeader ? `${headerHeight}px` : '0px',
            backgroundColor: 'grey.100',
            minHeight: '100vh'
        }}>
            {includeHeader && (
                <FormHeader
                    ref={headerRef}
                    color={idealCandidate.theme?.applicationHeaderColor || "#186BCC"}
                    idealCandidate={idealCandidate}
                    logo={idealCandidate.theme?.applicationLogoUrl}
                />
            )}
            <Box maxWidth="900px" id="form-container" sx={{ width: '100%', p: { xs: 2, sm: 3 }, mt: includeHeader ? 0 : 2.5, mb: 3 }}>
                {errors && errors.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                    {errors.map((error, index) => (
                        <Alert severity="error" variant="outlined" key={index} sx={{ mb: 1.5, borderRadius: '8px' }}>
                            {error.loc && error.loc.length > 0 ? `${error.loc[0]?.toUpperCase()} ${error.msg.toLowerCase()}` : error.msg}
                        </Alert>
                    ))}
                    </Box>
                )}

                {completed && showSuccess ? (
                    <FormSuccess idealCandidate={idealCandidate} includeHeader={false} />
                ) : completed && !showSuccess ? null : (
                    <>
                        <Paper variant="outlined" sx={{ borderRadius: '12px', p: { xs: 2, sm: 3 }, mb: 3 }}>
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 500, color: 'text.primary', mb: 3 }}>
                                Applicant Information
                            </Typography>
                            <Box id="candidate-identification-container">
                                <FormFieldWrapper label="Name">
                                    <FormField formik={formik} type="name" viewOnly={viewOnly} />
                                </FormFieldWrapper>
                                <FormFieldWrapper label="Email">
                                    <FormField formik={formik} type="email" viewOnly={viewOnly} />
                                </FormFieldWrapper>
                                <FormFieldWrapper label="Phone">
                                    <MuiTelInput
                                        defaultCountry="US"
                                        required
                                        fullWidth
                                        size="small"
                                        disabled={viewOnly}
                                        value={formik.values.phone}
                                        onChange={handlePhoneChange}
                                        onBlur={handlePhoneBlur}
                                        error={formik.touched.phone && Boolean(formik.errors.phone)}
                                        helperText={formik.touched.phone && formik.errors.phone}
                                        InputProps={{ sx: { borderRadius: '8px' } }} 
                                    />
                                </FormFieldWrapper>
                            </Box>
                        </Paper>

                        <Paper
                            variant="outlined"
                            sx={{
                                borderRadius: '12px',
                                p: { xs: 2, sm: 3 },
                                mb: 3,
                                borderLeft: `5px solid ${idealCandidate.theme?.applicationHeaderColor || '#0b57d0'}`, // Thematic accent border
                                pl: { xs: 2.5, sm: 3.5 } 
                            }}
                        >
                            <Box id="candidate-skills-container">
                                
                                <FormEvalSection
                                    dataLabel='skills'
                                    dataSet={idealCandidate.skills}
                                    formValues={formik.values.skills}
                                    handleUpdate={handleChange}
                                    idealCandidate={idealCandidate}
                                    label='Skills'
                                    viewOnly={viewOnly}
                                />
                            </Box>
                        </Paper>

                        <Paper
                            variant="outlined"
                            sx={{
                                borderRadius: '12px',
                                p: { xs: 2, sm: 3 },
                                mb: 3,
                                borderLeft: `5px solid ${idealCandidate.theme?.applicationHeaderColor || '#0b57d0'}`, // Thematic accent border
                                pl: { xs: 2.5, sm: 3.5 } 
                            }}
                        >
                            <Box id="candidate-behaviors-container">
                                <FormEvalSection
                                    dataLabel='behaviors'
                                    dataSet={idealCandidate.behaviors}
                                    formValues={formik.values.behaviors}
                                    handleUpdate={handleChange}
                                    idealCandidate={idealCandidate}
                                    label='Decision Making'
                                    viewOnly={viewOnly}
                                />
                            </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ borderRadius: '12px', p: { xs: 2, sm: 3 }, mb: 3 }}>
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                                Voluntary Self-Identification of Ethnicity, Race and Gender
                            </Typography>
                            {/* Content no longer needs special indenting for a removed icon */}
                            <Box id="candidate-self-identification-container" sx={{ pt: 1 /* Minor padding top if needed after title */ }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}> 
                                    We are an Affirmative Action, Equal Opportunity Employer. Our employment
                                    decisions are made without regard to race, color, religion, gender,
                                    national origin, age, disability, marital status, veteran or military
                                    status, or any other legally protected status. The purpose of this
                                    Employee EEO Self-Identification Form is to comply with federal government
                                    record-keeping and reporting requirements.
                                </Typography>
                                <FormRadioGroup
                                    displayRow
                                    dataSet={PCF_CONSTANTS.GENDER_VALUES}
                                    formik={formik}
                                    label="Gender"
                                    value="gender"
                                    viewOnly={viewOnly}
                                />
                                <FormRadioGroup
                                    dataSet={PCF_CONSTANTS.RACE_VALUES}
                                    formik={formik}
                                    label="Race"
                                    value="race"
                                    viewOnly={viewOnly}
                                />
                            </Box>
                        </Paper>

                        {!viewOnly && (
                            <Container>
                                <Divider sx={{ my: 4 }} />
                                <Grid container spacing={1} alignItems="flex-start" sx={{ mb: 1.5 }}>
                                    <Grid item xs={12} md={9}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                            <Checkbox
                                                checked={attestation}
                                                color="primary"
                                                onChange={(e) => setAttestation(e.target.checked)}
                                                sx={{ p: 0, mr: 1.5, mt: '-5px' }}
                                            />
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                                                By checking this box, I hereby attest and confirm that the answers
                                                and information I have provided are true and accurate to the best
                                                of my knowledge.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 2.5, mb:1 }}>
                                    <Button
                                        disabled={!handleValidateRequiredFields()}
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        sx={{
                                            backgroundColor: idealCandidate.theme?.applicationSubmitButtonColor || '#0b57d0',
                                            color: idealCandidate.theme?.applicationSubmitButtonTextColor || '#ffffff',
                                            padding: '10px 24px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderRadius: '100px', // Pill-shaped
                                            boxShadow: 'none',
                                            '&:hover': {
                                                backgroundColor: idealCandidate.theme?.applicationSubmitButtonHoverColor || '#0a50c2',
                                                boxShadow: 'none',
                                            }
                                        }}
                                    >
                                        Submit Application
                                    </Button>
                                </Box>
                                <Image src='/assets/ct_powered_by.png' alt="Powered By Candidate Tools Logo" />
                            </Container>
                        )}
                    </>
                )}
            </Box>
        </Box>
    </form>
);
}

PotentialCandidateForm.defaultProps = {
    includeHeader: true,
    showSuccess: true,
    viewOnly: false,
};

export default PotentialCandidateForm;